import { ref, computed, watch, inject } from 'vue';
import type { ComputedRef } from 'vue';
import { UserCourse } from './models/UserCourse';
import { UserSection } from 'modules/Section/models/UserSection';
import { UserStep } from 'modules/Step/models/UserStep';
import { useRepo } from 'pinia-orm';
import { useAssignments } from 'shared/composables/useAssignments';
import type { UserCourseProgress } from 'src/types/course/course';
import {
  useStrapiGet,
  useStrapiPost,
  useStrapiPut,
  useStrapiDelete,
  useMemoizedGet,
  getJwtFromLocalStorage,
} from 'shared/composables/useStrapiApi';
import type { CourseSection } from './models/CourseSection';
import type { TermsOfTransition } from 'src/types/course/termsOfTransition';
import { Course } from './models/Course';
import { createModuleLogger } from 'src/shared/utilities/logger';
import { usePremiumAccessGuard } from './composables/usePremiumAccessGuard';
import type { UserRole } from 'src/types/User';
import {
  calculateCourseAccumulatedTime,
  calculateCourseAverageScore,
} from './utilities/courseProgressMetrics';
import { hasCourseStarted } from './utilities/courseProgressState';
import { isUserCourseVisible } from './utilities/courseVisibility';
import {
  compareByListCriteria,
  matchesListCriteria,
  toListQueryParams,
} from 'shared/composables/useListFilters/useListFilters';
import type {
  FilterableListItem,
  ListFilterCriteria,
} from 'shared/composables/useListFilters/useListFilters';

const userSectionLog = createModuleLogger('UserSection');

export type Filter =
  | { key: 'is_favorite'; value: boolean }
  | { key: 'is_favorite_all'; value: boolean }
  | { key: 'is_assigned'; value: boolean }
  | { key: 'started_or_favorite'; value: boolean }
  | undefined;
// | { key: 'is_completed'; value: boolean }
// | { key: 'is_started'; value: boolean }
// | { key: 'tag'; value: string };

const {
  fetchUserCourses,
  isLastUserCourse,
  isLastFilteredUserCourse,
  userCourseTotal,
  getCourseFavoriteTotal,
} = useAssignments();
const userCourseRepo = useRepo(UserCourse);
const userSectionRepo = useRepo(UserSection);
const userStepRepo = useRepo(UserStep);
const courseRepo = useRepo(Course);

const courseCardHeaderBgData = ref<{ id: string; url: string } | null>(null);

async function removeStrapiUserSection(
  userSectionId: string,
): Promise<boolean> {
  const { execute, error: deleteError } = useStrapiDelete(
    `user-sections/${userSectionId}`,
  );
  try {
    await execute();
    return !deleteError.value;
  } catch (error) {
    console.error('Error deleting user section:', deleteError.value, error);
    return false;
  }
}

function removeRepoUserSection(userSectionId: string): boolean {
  const deletedUserSection = userSectionRepo
    .where('documentId', userSectionId)
    .delete();
  return !!deletedUserSection;
}

/** Adapter: a user-course row seen through the shared list-filter shape. */
export function toFilterableItem(userCourse: UserCourse): FilterableListItem {
  return {
    name: userCourse.course?.course_name ?? '',
    startDate: userCourse.start_date,
    completionDate: userCourse.completion_date,
    score: userCourse.score,
    timeAccumulator: userCourse.time_accumulator,
    progressCount: userCourse.progress?.sections?.length ?? 0,
  };
}

// TODO: Add pagination to the sections (currently pulls 25 sections)
export const useUserCourseData = ({
  userCourseDocId,
  filter,
  criteria,
}: {
  userCourseDocId?: string;
  filter?: Filter;
  criteria?: ComputedRef<ListFilterCriteria>;
} = {}) => {
  const currentUserRole =
    inject<ComputedRef<UserRole | undefined>>('currentUserRole');
  const isSchoolStudent = computed(() => currentUserRole?.value === 'student');

  const isLoading = ref(false);
  const { handlePremiumAccessDenied } = usePremiumAccessGuard();
  // const currentStepId = ref('');

  const userCourseByCurrentId = computed(() =>
    userCourseRepo
      .with('course', (query) => {
        query.withAllRecursive();
      })
      .where('documentId', userCourseDocId)
      .first(),
  );

  // Courses before list criteria are applied — the "M" of the bar's "N / M".
  const baseUserCourses = computed(() => {
    const allUserCourses = userCourseRepo.with('course').get();

    if (filter?.key && filter.value !== undefined) {
      return allUserCourses.filter((userCourse) => {
        if (filter.key === 'is_favorite') {
          return (
            userCourse.is_favorite === filter.value &&
            userCourse.is_assigned !== false
          );
        }

        if (filter.key === 'is_favorite_all') {
          return userCourse.is_favorite === filter.value;
        }

        if (filter.key === 'is_assigned') {
          return userCourse.is_assigned === filter.value;
        }

        if (filter.key === 'started_or_favorite') {
          return (
            hasCourseStarted(userCourse) || userCourse.is_favorite === true
          );
        }

        return false;
      });
    }

    return allUserCourses.filter((userCourse) =>
      isUserCourseVisible(userCourse, {
        isSchoolStudent: isSchoolStudent.value,
      }),
    );
  });

  // The list criteria are mirrored over the cache (same definitions the
  // backend applies — see useListFilters) so the display reacts instantly and
  // cached records from other pages can never leak into a filtered view.
  const userCourses = computed(() => {
    if (!criteria?.value) return baseUserCourses.value;
    const current = criteria.value;
    return baseUserCourses.value
      .filter((userCourse) =>
        matchesListCriteria(toFilterableItem(userCourse), current),
      )
      .sort((a, b) =>
        compareByListCriteria(
          toFilterableItem(a),
          toFilterableItem(b),
          current.sortBy,
        ),
      );
  });

  // const currentUserCourse = computed(() => {
  //   return userCourseRepo.where('documentId', userCourseDocId);
  // });

  async function getUserCourses() {
    isLoading.value = true;
    if (filter?.key === 'is_favorite' || filter?.key === 'is_favorite_all') {
      await fetchUserCourses({
        is_favorite: filter.value,
        offset: 0,
      });
    } else if (filter?.key === 'is_assigned') {
      await fetchUserCourses({
        is_assigned: filter.value,
        offset: 0,
      });
    } else if (filter?.key === 'started_or_favorite') {
      await fetchUserCourses({
        offset: 0,
      });
    } else if (criteria?.value && criteria.value.statuses.length === 0) {
      // Empty status selection shows nothing — no point asking the server.
    } else {
      await fetchUserCourses(
        undefined,
        criteria?.value ? toListQueryParams(criteria.value) : {},
      );
    }
    isLoading.value = false;
  }

  const courseByCurrentId = computed(() => {
    return userCourseByCurrentId.value?.course;
  });

  const courseDocId = computed(() => {
    return userCourseByCurrentId.value?.course?.documentId;
  });

  watch(
    () => courseDocId.value,
    async (newVal) => {
      if (newVal) await fetchCourseSections();
    },
  );

  function getUpdateCourseData(userCourseData: Partial<UserCourse>) {
    const { progress, score, completion_date, time_accumulator, start_date } =
      userCourseData;
    const courseData = {};
    if (progress !== undefined) {
      if (progress)
        Object.assign(progress, { id: courseByCurrentId.value?.id ?? 0 });
      Object.assign(courseData, { progress });
    }
    if (score !== undefined) Object.assign(courseData, { score });
    if (completion_date !== undefined)
      Object.assign(courseData, { completion_date });
    if (time_accumulator !== undefined)
      Object.assign(courseData, { time_accumulator });
    // An explicit start_date from the caller wins (the reset path sends null).
    if (start_date !== undefined) {
      Object.assign(courseData, { start_date });
    } else if (userCourseByCurrentId.value) {
      // Only stamp a first-touch start_date when the record is actually loaded.
      // A missing record is not the same as "never started" — reading it as such
      // overwrote real start dates whenever the repo was flushed mid-session.
      // Omitting the field leaves the stored value untouched.
      Object.assign(courseData, {
        start_date:
          userCourseByCurrentId.value.start_date ?? new Date().toISOString(),
      });
    }
    return courseData;
  }

  function updateUserCourseRepo(userCourseData: Partial<UserCourse>) {
    if (!userCourseRepo?.value || !userCourseDocId) return;
    const courseData = getUpdateCourseData(userCourseData);
    userCourseRepo.where('documentId', userCourseDocId).update(courseData);
  }

  async function updateUserCourseStrapi(userCourseData: Partial<UserCourse>) {
    if (!userCourseRepo?.value || !userCourseDocId) return;
    if (!getJwtFromLocalStorage()) return;
    const courseData = getUpdateCourseData(userCourseData);

    const { execute } = useStrapiPut(
      `user-courses/${userCourseDocId}/progress`,
      {
        data: courseData,
      },
    );

    try {
      await execute();
    } catch (error) {
      if (handlePremiumAccessDenied(error)) return;
      console.error('updateUserCourseStrapi:', error);
    }
  }

  // function to remove user sections by ids
  function removeUserSection(userSectionIds: number[]) {
    if (!userSectionIds || userSectionIds.length === 0) return;
    userSectionRepo.destroy(userSectionIds);
  }

  async function resetUserCourseProgress() {
    if (!currentCourseProgress.value || !userCourseDocId) return;
    // Get the usersections from the usercourse progress
    const userSections = currentCourseProgress.value?.sections ?? [];
    const userSectionIds = userSections.map(
      (section) => section.user_section_id,
    );

    // Remove usersteps from the repo
    const userStepsDocIds = userSectionRepo
      .find(userSectionIds)
      .reduce((acc, userSection) => {
        const currStepDocIds = userSection.progress?.steps?.map(
          (step) => step.step_doc_id,
        );
        if (Array.isArray(currStepDocIds)) acc = [...acc, ...currStepDocIds];
        return acc;
      }, [] as string[]);

    const { execute, data } = useStrapiPost(
      `user-courses/${userCourseDocId}/reset`,
    );

    try {
      await execute();
      if (data.value) {
        // Remove usersteps from the repo
        userStepRepo.query().whereIn('documentId', userStepsDocIds).delete(); // Ensure userStepRepo is defined and used correctly

        // Remove user sections from the repo
        removeUserSection(userSectionIds);

        // Remove user sections from strapi
        updateUserCourseRepo({
          progress: null,
          score: 0,
          start_date: null,
          completion_date: null,
          time_accumulator: 0,
        });
      }
    } catch (error) {
      console.error('resetUserCourseProgress:', error);
    }
  }

  async function getUserSectionData(userSectionId: number) {
    const userSection = userSectionRepo.find(userSectionId);
    // Skip fetch if exists in pinia store
    if (userSection) return;

    const { execute, data } = useStrapiGet(
      `user-sections/by-id/${userSectionId}`,
    );

    try {
      await execute();
      if (data.value) {
        return userSectionRepo.save(data.value as UserSection);
      }
    } catch (error) {
      console.error('getUserSectionData:', error);
    }
    return undefined;
  }

  const userSectionByUserSectionId = (userSectionDocId: string) => {
    return userSectionRepo
      .with('section')
      .where('documentId', userSectionDocId)
      .first();
  };

  const sectionByUserSectionDocId = (userSectionDocId: string) => {
    if (!userSectionDocId) return undefined;
    return userSectionByUserSectionId(userSectionDocId)?.section;
  };

  // TODO: Add time accumulator to the sections when the step is completed
  const currentCourseProgress: ComputedRef<Partial<UserCourseProgress> | null> =
    computed(() => {
      return userCourseByCurrentId.value?.progress ?? {};
    });

  const currentCourseScore = computed(() => {
    return calculateCourseAverageScore(currentCourseProgress.value?.sections);
  });

  // TODO: Add time accumulator to the sections when the step is completed
  const currentCourseAccumulatedTime = computed(() => {
    return calculateCourseAccumulatedTime(
      currentCourseProgress.value?.sections,
    );
  });

  function getProgressCourseSection(sectionDocId: string) {
    if (!currentCourseProgress.value) return undefined;
    return currentCourseProgress.value.sections?.find(
      (section) => section.section_doc_id === sectionDocId,
    );
  }

  async function fetchCourseSections() {
    const targetDocumentId = userCourseDocId ?? courseDocId.value;
    if (!targetDocumentId) return;

    const { execute, data, error } = useStrapiGet(
      `user-courses/${targetDocumentId}/sections`,
      {
        offset: courseSections.value?.length ?? 0,
      },
    );

    try {
      await execute();
      if (!error.value && data.value) {
        console.log('data:', data.value);
        courseRepo.save(data.value);
        return data.value;
      }
    } catch (err) {
      console.error(
        `fetchCourseSections: Error fetching sections: ${err as string}`,
      );
    }
  }

  async function getCourseCardHeaderBg() {
    if (courseCardHeaderBgData.value?.url)
      return courseCardHeaderBgData.value.url;
    const res = await useMemoizedGet(`upload/files`, {
      fields: ['url'],
      filters: {
        name: {
          $eq: 'course_card_header_bg.png',
        },
      },
    });
    courseCardHeaderBgData.value = res.data.value?.[0];
    if (res.error.value) {
      console.error(
        'getCourseCardHeaderBg: Error fetching background image: ',
        res.error.value,
      );
    }
  }

  const courseSections = computed(() => {
    return (
      courseByCurrentId.value?.course_sections
        ?.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
        .map((courseSection: CourseSection) => courseSection.section) ?? []
    );
  });

  function getCourseSection(sectionId: number) {
    return courseByCurrentId.value?.course_sections.find(
      (courseSection) => courseSection?.section_id === sectionId,
    );
  }

  function getCourseSectionsTermsOfTransition(
    sectionId: number,
  ): TermsOfTransition[] {
    return getCourseSection(sectionId)?.terms_of_transition ?? [];
  }

  async function setCourseUserSectionDocId(
    sectionDocId: string,
    userSectionId: number,
  ) {
    if (!currentCourseProgress.value) {
      if (getProgressCourseSection(sectionDocId)) return;
      const progress = {
        id: 0,
        length: courseSectionsCount.value,
        sections: [
          {
            section_doc_id: sectionDocId,
            user_section_id: userSectionId,
            start_date: new Date().toISOString(),
          },
        ],
      };

      updateUserCourseRepo({ progress });
      await updateUserCourseStrapi({ progress });
      return;
    }

    const currentSections = currentCourseProgress.value?.sections ?? [];
    Object.assign(currentCourseProgress.value, {
      sections: [
        ...currentSections,
        { section_doc_id: sectionDocId, user_section_id: userSectionId },
      ],
    });
    updateUserCourseRepo({ progress: currentCourseProgress.value });
    await updateUserCourseStrapi({ progress: currentCourseProgress.value });
  }

  async function createUserSection(courseId: number, sectionDocId: string) {
    if (!sectionDocId) return;
    // Duplicate prevention: check local progress and repo first
    const existingProgressEntry = getProgressCourseSection(sectionDocId);
    if (existingProgressEntry?.user_section_id) {
      userSectionLog.info('Reusing progress user_section_id', {
        sectionDocId,
        userSectionId: existingProgressEntry.user_section_id,
        source: 'progress-cache',
        action: 'reuse',
      });
      return existingProgressEntry.user_section_id;
    }

    // Check local repo for a user section already associated to this section
    const existingRepoUserSection = userSectionRepo
      .with('section')
      .where((record) => {
        return (
          record.user_course_id === courseId &&
          record.section?.documentId === sectionDocId
        );
      })
      .first();
    if (existingRepoUserSection?.id) {
      userSectionLog.info('Reusing repo user section', {
        sectionDocId,
        userSectionId: existingRepoUserSection.id,
        source: 'repo-cache',
        action: 'reuse',
      });
      return existingRepoUserSection.id;
    }

    const lookupExistingMine = async () => {
      const {
        execute: executeMine,
        data: dataMine,
        error: errorMine,
      } = useStrapiGet('user-sections-me', {
        offset: 0,
        sectionDocumentId: sectionDocId,
        userCourseId: courseId,
      });

      await executeMine();

      if (errorMine.value) {
        return undefined;
      }

      const mineHit = dataMine.value?.[0]?.[0];
      if (!mineHit?.id) {
        return undefined;
      }

      userSectionRepo.save(mineHit as UserSection);
      return mineHit.id as number;
    };

    // Ownership-safe preflight for roles without broad user-sections read permission.
    try {
      const mineHitId = await lookupExistingMine();
      if (mineHitId) {
        userSectionLog.info('Reusing mine preflight user section', {
          sectionDocId,
          userSectionId: mineHitId,
          source: 'mine-preflight',
          action: 'preflight-hit',
        });
        return mineHitId;
      }
    } catch (e) {
      userSectionLog.warn(
        'Mine preflight lookup failed, will attempt creation',
        {
          sectionDocId,
          action: 'mine-preflight-failed',
          error: (e as Error).message,
        },
      );
    }

    const userSectionData = {
      sectionDocumentId: sectionDocId,
      user_course_id: courseId,
      start_date: new Date().toISOString(),
    };

    const { execute, data } = useStrapiPost('user-sections', {
      data: userSectionData,
    });
    try {
      await execute();
      if (data.value?.data?.id) {
        // Fetch complete user section data
        const userSection = await getUserSectionData(data.value.data.id);
        if (userSection) {
          return data.value.data.id;
        }
      }
    } catch (error) {
      userSectionLog.error('Error creating user section, attempting recovery', {
        sectionDocId,
        action: 'create-error',
        error,
      });

      // First recovery path: ownership-safe lookup in my user-sections.
      try {
        const mineHitId = await lookupExistingMine();
        if (mineHitId) {
          userSectionLog.info('Recovered mine after creation error', {
            sectionDocId,
            userSectionId: mineHitId,
            source: 'mine-recovery',
            action: 'recovery-hit',
          });
          return mineHitId;
        }
      } catch (mineRecoveryError) {
        userSectionLog.error('Mine recovery lookup failed', {
          sectionDocId,
          action: 'mine-recovery-failed',
          error: mineRecoveryError,
        });
      }
    }
    return undefined;
  }

  async function generateUserSection(sectionDocId: string | undefined) {
    if (!sectionDocId) return undefined;

    const courseSection = getProgressCourseSection(sectionDocId);
    const userCourseId = userCourseByCurrentId.value?.id;
    let userSectionId = courseSection?.user_section_id;
    if (!courseSection && userCourseId) {
      userSectionId = await createUserSection(+userCourseId, sectionDocId);

      if (userSectionId) {
        await setCourseUserSectionDocId(sectionDocId, userSectionId); // in the progress
        await fetchCourseSections(); // Refresh course sections after creation
      }
    }
    return userSectionId;
  }

  // Progress functionality
  // Number of steps
  const courseSectionsCount = computed(() =>
    courseSections?.value ? courseSections.value?.length : 0,
  );

  // Completed Sections
  const completedSectionsCount = computed(
    () =>
      currentCourseProgress?.value?.sections?.filter(
        (section) => section.completion_date,
      )?.length ?? 0,
  );

  // Completed Course
  // A zero section count means the course's sections are not in the repo yet
  // (or were flushed out from under us) — not that every section is done.
  // Without the `> 0` guard `0 === 0` reads as complete and stamps a phantom
  // completion_date on a course the user never finished.
  const isCompletedCourse = computed(
    () =>
      courseSectionsCount.value > 0 &&
      completedSectionsCount.value === courseSectionsCount.value,
  );

  // Course ProgressPercent
  const currentCourseProgressPercent = computed(() =>
    Math.round(
      (completedSectionsCount?.value / courseSectionsCount?.value) * 100,
    ),
  );

  const courseProgressPercent = (userCourse: UserCourse) => {
    const res = {
      totalSections: 0,
      completedSections: 0,
      percent: 0,
    };
    if (!userCourse || !userCourse.course) return res;
    const course = courseRepo
      .with('course_sections')
      .find(userCourse.course.id);
    if (!course) return res;
    res.totalSections = course.course_sections?.length ?? 0;
    if (res.totalSections === 0) return res;
    res.completedSections =
      userCourse.progress?.sections?.filter(
        (section) => section.completion_date,
      )?.length ?? 0;
    res.percent = Math.round((res.completedSections / res.totalSections) * 100);
    return res;
  };

  // Accumulated Time
  const accumulatedTime = computed(() => {
    return currentCourseProgress?.value?.sections?.reduce((acc, section) => {
      return acc + (section.time_accumulator ?? 0);
    }, 0);
  });

  // completed steps ids
  const completedSectionsId = computed(() => {
    if (!currentCourseProgress.value) return [];
    const completedSectionsId =
      currentCourseProgress.value?.sections
        ?.filter((section) => section.completion_date)
        ?.map((section) => section.section_doc_id) ?? [];
    return completedSectionsId;
  });

  function getProgressSectionData(sectionDocId: string | null | undefined) {
    if (!currentCourseProgress.value) return undefined;
    return currentCourseProgress.value.sections?.find(
      (section) => section.section_doc_id === sectionDocId,
    );
  }

  // Next Section Step
  const nextSectionStep = computed(() => {
    if (!currentCourseProgress.value) return courseSections.value?.[0];
    const nextStep = courseSections.value?.find(
      (section) =>
        !completedSectionsId.value.includes(section?.documentId ?? ''),
    );
    return nextStep;
  });

  // Next Course Section
  const nextCourseSection = computed(() => {
    if (!courseSections.value || courseSections.value.length === 0) return null;

    // Find the first section that doesn't have a completion date
    const nextSection = courseSections.value.find((section) => {
      const sectionProgress = getProgressSectionData(section?.documentId);
      return !sectionProgress?.completion_date;
    });

    // If all sections are completed, return the first section
    // If no sections are started yet, return the first section
    return nextSection || courseSections.value[0];
  });

  // Section Score
  const userCourseSectionScore = (sectionDocId: string) => {
    if (!currentCourseProgress.value) return undefined;
    return (
      currentCourseProgress.value.sections?.find(
        (section) => section.section_doc_id === sectionDocId,
      )?.score ?? 0
    );
  };

  watch(
    () => isCompletedCourse.value,
    async (isCompleted) => {
      if (isCompleted) {
        // Update the user course with the completion date, score and accumulated time
        console.log('Course has been completed.');
        await handleCourseCompletion();
      }
    },
  );

  async function handleCourseCompletion() {
    if (!isCompletedCourse.value) return;
    // The completion write reads score/time off this record; if it is missing
    // the repo was flushed mid-session and we would persist a completion built
    // from empty state.
    if (!userCourseByCurrentId.value) return;
    // Update the user course with the completion date, score and accumulated time
    console.log('Course has been completed.');
    const completionDate =
      userCourseByCurrentId.value?.completion_date ?? new Date().toISOString();
    const score =
      userCourseByCurrentId.value?.score || currentCourseScore.value;
    const timeAccumulator =
      userCourseByCurrentId.value?.time_accumulator ||
      currentCourseAccumulatedTime.value;
    await updateUserCourseStrapi({
      completion_date: completionDate,
      score,
      time_accumulator: timeAccumulator,
    });
    updateUserCourseRepo({
      completion_date: completionDate,
      score,
      time_accumulator: timeAccumulator,
    });
  }

  async function favoriteCourse(userCourseDocId: string, isFavorite: boolean) {
    if (!userCourseDocId) return;
    const { execute, error } = useStrapiPost(
      `user-courses/${userCourseDocId}/favorite`,
      { is_favorite: isFavorite },
    );
    try {
      await execute();
      if (error.value) {
        console.error('Error favoriting course:', error.value);
      } else {
        // Update the user course in the repo
        const userCourse = userCourseRepo
          .where('documentId', userCourseDocId)
          .first();
        if (userCourse) {
          userCourse.is_favorite = isFavorite;
          userCourseRepo.save(userCourse);
          void getCourseFavoriteTotal(true);
        }
      }
    } catch (error) {
      console.error('Error favoriting course:', error);
    }
  }

  /**
   * Remove a user course from Strapi backend and update the Pinia stores.
   */
  async function removeUserCourse(userCourseDocId: string): Promise<boolean> {
    if (!userCourseDocId) return false;
    const { execute, error } = useStrapiDelete(
      `user-courses/${userCourseDocId}`,
    );
    try {
      await execute();
      if (!error.value) {
        // Remove from Pinia store
        const res = userCourseRepo
          .where('documentId', userCourseDocId)
          .delete();
        // Set user_course_id to null in the courseRepo for the related course
        if (res && res.length > 0) {
          courseRepo
            .query()
            .where('user_course_id', res[0]?.id)
            .update({ user_course_id: null });
          if (userCourseTotal.value)
            userCourseTotal.value = userCourseTotal.value - 1;
        }

        return true;
      } else {
        console.error('Error removing user course:', error.value);
        return false;
      }
    } catch (err) {
      console.error('Error removing user course:', err);
      return false;
    }
  }

  return {
    currentCourseProgress,
    currentCourseScore,
    currentCourseAccumulatedTime,
    isLastUserCourse,
    isLastFilteredUserCourse,
    userCourseTotal,
    isLoading,
    userCourses,
    baseUserCourses,
    courseByCurrentId,
    userCourseByCurrentId,
    courseDocId,
    courseSections,
    updateUserCourseRepo,
    updateUserCourseStrapi,
    getUserSectionData,
    setCourseUserSectionDocId,
    getCourseSection,
    getProgressCourseSection,
    fetchCourseSections,
    getUserCourses,
    createUserSection,
    generateUserSection,
    getProgressSectionData,
    getCourseSectionsTermsOfTransition,
    removeStrapiUserSection,
    removeRepoUserSection,
    handleCourseCompletion,
    favoriteCourse,
    removeUserCourse,
    courseProgressPercent,

    // Progress
    //removeUserSectionProgress,
    courseCardHeaderBgData,
    getCourseCardHeaderBg,
    resetUserCourseProgress,
    courseSectionsCount,
    completedSectionsCount,
    isCompletedCourse,
    currentCourseProgressPercent,
    accumulatedTime,
    completedSectionsId,
    nextSectionStep,
    nextCourseSection,
    userCourseSectionScore,
    sectionByUserSectionDocId,
    userSectionByUserSectionId,
  };
};
